import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, TextField, Button, Typography, InputAdornment,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, CircularProgress, Tooltip, Pagination, MenuItem, IconButton,
  Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogContentText,
  DialogActions,
} from "@mui/material";
import {
  Search, Refresh, ChevronRight, VerifiedUser, ContentCopy, Check, DeleteForever,
} from "@mui/icons-material";
import { gqlRequest, GET_BUSINESSES_WITH_PAGING } from "../../api/graphql";
import { useAuth } from "../../store/AuthContext";
import { fmtDate } from "../../utils/helpers";
import { COLORS } from "../../theme";
import { PageWrapper, PageHeader, BizAvatar, SectionCard, EmptyState } from "../../components/UI";

const ROWS_PER_PAGE_OPTIONS = [10, 20, 50, 100];
const SEARCH_DEBOUNCE_MS = 400;
const TABLE_MAX_HEIGHT = 560;

const DELETE_ALL_BUSINESS_DATA = `
  mutation DeleteAllBusinessData($businessid: ID!) {
    deleteAllBusinessData(businessid: $businessid)
  }
`;

// Trim outer whitespace + collapse internal runs of whitespace to one space.
// Handles invisible chars like non-breaking spaces (\u00a0) from the DB.
const normalizeName = (s) =>
  String(s || "")
    .replace(/[\u00a0\u200b\u200c\u200d\ufeff]/g, " ") // replace common invisible chars
    .trim()
    .replace(/\s+/g, " ");

function CopyEmailButton({ email, onDelete }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e) => {
    e.stopPropagation();
    if (!email) return;
    try {
      await navigator.clipboard.writeText(email);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // fallback silent fail
    }
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      {email ? (
        <>
          <Typography variant="body2" sx={{ color: COLORS.muted }}>{email}</Typography>
          <Tooltip title={copied ? "Copied!" : "Copy email"}>
            <IconButton
              size="small"
              onClick={handleCopy}
              sx={{
                p: 0.3,
                color: copied ? "success.main" : COLORS.muted,
                opacity: 0.6,
                "&:hover": { opacity: 1, bgcolor: "action.hover" },
                transition: "all 0.15s",
              }}
            >
              {copied ? <Check sx={{ fontSize: 13 }} /> : <ContentCopy sx={{ fontSize: 13 }} />}
            </IconButton>
          </Tooltip>
        </>
      ) : (
        <Typography variant="body2" sx={{ color: COLORS.muted }}>—</Typography>
      )}

      <Tooltip title="Delete all business data">
        <IconButton
          size="small"
          onClick={e => { e.stopPropagation(); onDelete(); }}
          sx={{
            p: 0.3,
            color: "error.main",
            opacity: 0.45,
            "&:hover": { opacity: 1, bgcolor: "error.lighter" },
            transition: "all 0.15s",
          }}
        >
          <DeleteForever sx={{ fontSize: 15 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

function DeleteConfirmDialog({ open, biz, onClose, onConfirm, deleting }) {
  const [confirmText, setConfirmText] = useState("");

  // Normalize the stored name so invisible/extra whitespace never
  // causes a silent mismatch when the user types the name correctly.
  const bizName = normalizeName(biz?.businessname) || "Unnamed";
  const isMatch = normalizeName(confirmText) === bizName;

  useEffect(() => {
    if (open) setConfirmText("");
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={!deleting ? onClose : undefined}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, p: 0.5 } }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, color: "error.main", fontWeight: 700 }}>
        <DeleteForever sx={{ fontSize: 22 }} />
        Delete Business Data
      </DialogTitle>

      <DialogContent>
        <DialogContentText sx={{ mb: 2, fontSize: 14, color: COLORS.textSub }}>
          This will <strong style={{ color: "inherit" }}>permanently delete all data</strong> for{" "}
          <strong>{bizName}</strong>, including contacts, campaigns, automations, and usage history.
          This action <strong style={{ color: "inherit" }}>cannot be undone.</strong>
        </DialogContentText>

        <Typography variant="caption" sx={{ color: COLORS.textSub, display: "block", mb: 0.75 }}>
          Type <strong>{bizName}</strong> to confirm:
        </Typography>
        <TextField
          autoFocus
          fullWidth
          size="small"
          placeholder={bizName}
          value={confirmText}
          onChange={e => setConfirmText(e.target.value)}
          disabled={deleting}
          // Show green border as soon as the text matches so the user
          // gets instant visual feedback without having to click Delete.
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 1.5,
              ...(isMatch && {
                "& fieldset": { borderColor: "success.main", borderWidth: 2 },
              }),
            },
          }}
          onKeyDown={e => { if (e.key === "Enter" && isMatch && !deleting) onConfirm(); }}
          // Inline helper so the user knows exactly what to type
          helperText={
            confirmText && !isMatch
              ? `Expected: "${bizName}"`
              : " "
          }
          FormHelperTextProps={{
            sx: {
              color: confirmText && !isMatch ? "error.main" : "transparent",
              mt: 0.5, fontSize: 11,
            },
          }}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button
          onClick={onClose}
          disabled={deleting}
          sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={onConfirm}
          disabled={!isMatch || deleting}
          startIcon={deleting ? <CircularProgress size={18} color="inherit" /> : <DeleteForever />}
          sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2, minWidth: 140 }}
        >
          {deleting ? "Deleting…" : "Delete All Data"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function BusinessesPage() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [businesses, setBusinesses] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const debounceRef = useRef(null);

  const [deleteBiz, setDeleteBiz] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });

  const load = useCallback(async (pg = 1, size = pageSize, searchText = search) => {
    setLoading(true);
    try {
      const data = await gqlRequest(
        GET_BUSINESSES_WITH_PAGING,
        { pageNumber: pg, pageSize: size, searchText: searchText || undefined },
        token
      );
      const result = data.businessesWithPaging;
      setBusinesses(result?.rows || []);
      setTotalCount(result?.count || 0);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [token, pageSize, search]);

  useEffect(() => { load(page, pageSize, search); }, [page, pageSize]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1);
      load(1, pageSize, search);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(debounceRef.current);
  }, [search]);

  const handlePageSizeChange = (e) => {
    const size = Number(e.target.value);
    setPageSize(size);
    setPage(1);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteBiz) return;
    setDeleting(true);
    try {
      await gqlRequest(
        DELETE_ALL_BUSINESS_DATA,
        { businessid: deleteBiz.entityid },
        token
      );
      setToast({
        open: true,
        message: `All data for "${normalizeName(deleteBiz.businessname) || "Unnamed"}" has been deleted.`,
        severity: "success",
      });
      setDeleteBiz(null);
      load(page, pageSize, search);
    } catch (e) {
      console.error(e);
      setToast({ open: true, message: "Delete failed. Please try again.", severity: "error" });
    } finally {
      setDeleting(false);
    }
  };

  const pageCount = Math.max(1, Math.ceil(totalCount / pageSize));

  return (
    <PageWrapper>
      <PageHeader
        title="Businesses"
        subtitle="Select a business to view and manage plan usage."
        action={
          <Button
            variant="outlined" startIcon={<Refresh />}
            onClick={() => load(page, pageSize, search)} disabled={loading}
            sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600, borderColor: COLORS.border, color: COLORS.muted }}
          >
            Refresh
          </Button>
        }
      />

      {/* Search */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2.5 }}>
        <TextField
          size="small"
          placeholder="Search by business name…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          sx={{ width: 280, "& .MuiOutlinedInput-root": { borderRadius: 1.5, bgcolor: COLORS.card } }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 18, color: COLORS.textSub }} /></InputAdornment>,
            endAdornment: loading && search && (
              <InputAdornment position="end"><CircularProgress size={14} /></InputAdornment>
            ),
          }}
        />
      </Box>

      {!loading && businesses.length === 0 ? (
        <EmptyState
          icon="🏢"
          title="No businesses found"
          desc={search ? "Try a different search term." : "Click Refresh to load businesses."}
          action={
            !search && (
              <Button variant="contained" onClick={() => load(page, pageSize, search)}
                sx={{ bgcolor: COLORS.accent, borderRadius: 2, textTransform: "none", fontWeight: 600 }}>
                Load Businesses
              </Button>
            )
          }
        />
      ) : (
        <SectionCard>
          <Box sx={{ position: "relative" }}>
            {loading && (
              <Box sx={{
                position: "absolute", inset: 0, zIndex: 2,
                display: "flex", alignItems: "center", justifyContent: "center",
                bgcolor: "rgba(255,255,255,0.6)", minHeight: TABLE_MAX_HEIGHT,
              }}>
                <CircularProgress sx={{ color: COLORS.accent }} />
              </Box>
            )}

            <TableContainer sx={{ maxHeight: TABLE_MAX_HEIGHT, overflowY: "auto" }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Business</TableCell>
                    <TableCell>Owner</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell>Joined</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {businesses.map(b => (
                    <TableRow
                      key={b.entityid}
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() => navigate(`/businesses/${b.entityid}/usage`, { state: { biz: b } })}
                    >
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                          <BizAvatar biz={b} />
                          <Box>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {b.businessname || "Unnamed"}
                              </Typography>
                              {b.isbusinessverified && (
                                <Tooltip title="Verified business">
                                  <VerifiedUser sx={{ fontSize: 14, color: COLORS.accent }} />
                                </Tooltip>
                              )}
                            </Box>
                            {b.brandname && (
                              <Typography variant="caption" sx={{ color: COLORS.textSub }}>{b.brandname}</Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {[b.firstname, b.lastname].filter(Boolean).join(" ") || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box onClick={e => e.stopPropagation()}>
                          <CopyEmailButton
                            email={b.email}
                            onDelete={() => setDeleteBiz(b)}
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: COLORS.muted }}>{b.phonenumber || "—"}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ color: COLORS.textSub }}>{fmtDate(b.createdAt)}</Typography>
                      </TableCell>
                      <TableCell>
                        {b.isfreebusiness
                          ? <Chip size="small" label="Free" sx={{ bgcolor: COLORS.warnLight, color: COLORS.warn, fontWeight: 700, fontSize: 11 }} />
                          : <Chip size="small" label="Paid" sx={{ bgcolor: COLORS.successLight, color: COLORS.success, fontWeight: 700, fontSize: 11 }} />
                        }
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small" endIcon={<ChevronRight />}
                          onClick={e => { e.stopPropagation(); navigate(`/businesses/${b.entityid}/usage`, { state: { biz: b } }); }}
                          sx={{ textTransform: "none", fontWeight: 600, color: COLORS.accent, fontSize: 12 }}
                        >
                          Manage
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* Bottom bar */}
          <Box sx={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            p: 2, borderTop: `1px solid ${COLORS.border}`, flexWrap: "wrap", gap: 1.5,
          }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="caption" sx={{ color: COLORS.textSub }}>Rows per page</Typography>
              <TextField
                select size="small" value={pageSize} onChange={handlePageSizeChange}
                sx={{ width: 80, "& .MuiOutlinedInput-root": { borderRadius: 1.5, bgcolor: COLORS.card } }}
              >
                {ROWS_PER_PAGE_OPTIONS.map(opt => (
                  <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                ))}
              </TextField>
            </Box>

            <Pagination
              count={pageCount} page={page} onChange={(_, v) => setPage(v)}
              size="small" shape="rounded" showFirstButton showLastButton
              sx={{ "& .MuiPaginationItem-root": { borderRadius: 1.5, fontWeight: 600 } }}
            />
          </Box>
        </SectionCard>
      )}

      <DeleteConfirmDialog
        open={!!deleteBiz}
        biz={deleteBiz}
        onClose={() => setDeleteBiz(null)}
        onConfirm={handleDeleteConfirm}
        deleting={deleting}
      />

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast(t => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={toast.severity}
          onClose={() => setToast(t => ({ ...t, open: false }))}
          sx={{ borderRadius: 2, fontWeight: 600 }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </PageWrapper>
  );
}